import React, { useState, useEffect, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Button, Popover, notification } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faCircleInfo, faWarning } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// FLAT BUFFERS
import { ElevatorControl } from "../../../flatbuffers/generated/control/elevator-control";
// CUSTOM HOOKS
import usePostCommand from "../../../customHooks/POST_requests/usePostCommand";
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// COMPONENTS
import ActionButtonPopover from "./ActionButtonPopover";
// INTERFACES
import { RootState } from "../../../app/store";

function ActionButtonEnergizeTurnback({
	validStates,
	toggleValidStates,
	label,
	toggleLabel,
	messageId,
	toggleMessageId,
	colorAction,
	timerAction,
	carService,
}: {
	validStates: string[];
	toggleValidStates: string[];
	label: string;
	toggleLabel: string;
	messageId: string;
	toggleMessageId: string;
	colorAction: string;
	timerAction: number;
	carService: number;
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const [t] = useTranslation("global");
	const TIMEOUT = 3;

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [loading, setLoading] = useState(false);
	const [counter, setCounter] = useState(0); // Initial counter value
	const [lastCommandSent, setLastCommandSent] = useState<string>("ON");
	const [checkIfCommandChange, setCheckIfCommandChange] = useState<boolean>(false);
	const [localCarService, setLocalCarService] = useState<number>(0);

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme, userLogged } = useSelector((state: RootState) => state.home);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */
	const { triggerPostCommand, isLoadingPostCommand, isSuccessPostCommand, isErrorPostCommand } = usePostCommand();
	const { messages } = useGetAllMessages();

	// ************************************************ */
	// MEMOIZED VALUES ******************************** */
	const messageEnum = useMemo(() => {
		const msg = messages.find(m => m.id === messageId);
		return msg?.short_message ?? "";
	}, [messages, messageId]);

	const toggleMessageEnum = useMemo(() => {
		const msg = messages.find(m => m.id === toggleMessageId);
		return msg?.short_message ?? "";
	}, [messages, toggleMessageId]);

	const validStatesNumbers = useMemo(() =>
		validStates.map(id => {
			const msg = messages.find(m => m.id === id);
			return msg?.enum_id ?? 0;
		}).filter(enumId => enumId !== 0)
		, [messages, validStates]);

	const toggleValidStatesNumbers = useMemo(() =>
		toggleValidStates.map(id => {
			const msg = messages.find(m => m.id === id);
			return msg?.enum_id ?? 0;
		}).filter(enumId => enumId !== 0)
		, [messages, toggleValidStates]);

	const messageEnumId = useMemo(() => {
		const msg = messages.find(m => m.id === messageId);
		if (!msg?.short_message) return 0;
		// Map short_message to ElevatorControl enum
		const enumValue = ElevatorControl[msg.short_message as keyof typeof ElevatorControl];
		return enumValue !== undefined ? enumValue : 0;
	}, [messages, messageId]);

	const toggleMessageEnumId = useMemo(() => {
		const msg = messages.find(m => m.id === toggleMessageId);
		if (!msg?.short_message) return 0;
		// Map short_message to ElevatorControl enum
		const enumValue = ElevatorControl[msg.short_message as keyof typeof ElevatorControl];
		return enumValue !== undefined ? enumValue : 0;
	}, [messages, toggleMessageId]);

	// ************************************************ */
	// FUNCTIONS ************************************** */
	// eslint-disable-next-line arrow-body-style
	const formatCamelCaseLabel = (value: string): string => {
		return value.replace(/([a-z])([A-Z])/g, "$1 $2");
	};

	const onClickTimedButton = () => {
		setLastCommandSent("ON");
		triggerPostCommand({
			floor: 0,
			command: messageEnumId,
			liftType: "ELEVATOR",
			active: true,
		});
	};

	const launchToggleAction = () => {
		setLastCommandSent("OFF");
		triggerPostCommand({
			floor: 0,
			command: toggleMessageEnumId,
			liftType: "ELEVATOR",
			active: true,
		});
	};

	const launchTimed = () => {
		setLoading(true);
		let count = timerAction; // Counter starts from 5
		setCounter(count); // Update counter state
		const countdownInterval = setInterval(() => {
			// eslint-disable-next-line operator-assignment
			count = count - 1;
			setCounter(count);
			if (count === 0) {
				clearInterval(countdownInterval);
				setLoading(false);
			}
		}, 1000); // Update counter every second
		setTimeout(() => {
			clearInterval(countdownInterval);
			setLoading(false);
			setLastCommandSent("OFF");
			triggerPostCommand({
				floor: 0,
				command: toggleMessageEnumId,
				liftType: "ELEVATOR",
				active: true,
			});
		}, timerAction * 1000); // Stop countdown and loading indicator after 5 seconds
	};

	const launchMessage = (errorCommand: boolean) => {
		const errorMessage = "Error Command";
		if (userLogged.command_notifications) {
			notification.open({
				message: (
					<span className={errorCommand ? "generalStyles__error" : "generalStyles__success"}>
						{lastCommandSent === "ON" ? label : toggleLabel}
					</span>
				),
				description: errorCommand ? (
					errorMessage
				) : (
					<span>
						{t("general.command")} {t("general.sentSuccessfully")}
					</span>
				),
				placement: "top",
				className: `monitoring__notification__${theme}`,
				icon: (
					<FontAwesomeIcon
						icon={errorCommand ? faWarning : faCheckCircle}
						fontSize={18}
						className={`monitoring__modalSettings__icon ${errorCommand ? "generalStyles__error" : "generalStyles__success"
							}`}
					/>
				),
			});
		}
	};

	// ************************************************ */
	// USE EFFECT ************************************* */
	useEffect(() => {
		if (isSuccessPostCommand) {
			if (lastCommandSent === "ON") {
				launchTimed();
				launchToggleAction();
			} else if (lastCommandSent === "OFF") {
				setTimeout(() => {
					setCheckIfCommandChange(true);
				}, TIMEOUT * 1000);
			}
		}
		if (isErrorPostCommand) {
			launchMessage(true);
		}
	}, [isSuccessPostCommand, isErrorPostCommand]);

	useEffect(() => {
		setLocalCarService(carService);
	}, [carService]);

	useEffect(() => {
		if (checkIfCommandChange) {
			setCheckIfCommandChange(false);
			if (
				lastCommandSent === "OFF" &&
				(toggleValidStatesNumbers.includes(localCarService) || validStatesNumbers.length === 0)
			) {
				launchMessage(false);
			} else {
				launchMessage(true);
			}
		}
	}, [checkIfCommandChange]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div className='generalStyles__flexCenter' style={{ marginBottom: "10px" }}>
			<div>
				<Popover
					content={
						<ActionButtonPopover
							label={label}
							toggleLabel={toggleLabel}
							validStates={validStates}
							toggleValidStates={toggleValidStates}
							type='TURNBACK'
							timer1={timerAction}
							timer2={0}
							message={messageEnum}
							toggleMessage={toggleMessageEnum}
							color={colorAction}
							toggleColor={colorAction}
						/>
					}
					title="TURNBACK"
				>
					<FontAwesomeIcon icon={faCircleInfo} fontSize={18} className='generalStyles__warning generalStyles__mrFix' />
				</Popover>
			</div>
			<div>
				<Button
					className={
						validStatesNumbers.includes(localCarService) || isLoadingPostCommand || loading
							? "buttonStyle__basicButtonActionInactive"
							: "buttonStyle__basicButtonAction"
					}
					loading={loading || isLoadingPostCommand}
					onClick={onClickTimedButton}
					disabled={validStatesNumbers.includes(localCarService) || loading || isLoadingPostCommand}
					style={{ backgroundColor: `${colorAction}`, width: "210px" }}
				>
					{loading ? (
						<>
							{formatCamelCaseLabel(toggleMessageEnum)}... {counter}s
						</>
					) : (
						<span className='generalStyles__textEllipsis' style={{ marginLeft: "5px" }}>
							{formatCamelCaseLabel(messageEnum)}
						</span>
					)}
				</Button>
			</div>
		</div>
	);
}

export default ActionButtonEnergizeTurnback;
